package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic
import kotlin.math.max

/**
 * 悬浮提示（tooltip）渲染器。
 *
 * 负责把候选元素的 tooltip 文本按主题样式绘制到屏幕上，含背景、圆角与多行排版。
 */
internal object TooltipRenderer {

    private const val TEXT_SCALE = 1f
    private const val TEXT_HEIGHT = 10f
    private const val GAP = 4f

    @JvmStatic
    @JvmName("render")
    internal fun render(tooltip: Candidate, bridge: UiRenderBridge, currentTimeMs: Long) {
        renderText(tooltip.text, tooltip.box, tooltip.hoverSinceMs, bridge, currentTimeMs)
    }

    @JvmStatic
    @JvmName("renderText")
    internal fun renderText(text: String?, box: Box?, hoverSinceMs: Long, bridge: UiRenderBridge, currentTimeMs: Long) {
        val t = ThemeAccess.current()
        if (!readyToRender(text, hoverSinceMs, t, currentTimeMs)) {
            return
        }
        if (box == null) return
        // 经 readyToRender 校验后 text 必非空
        val lines = splitLines(text!!)
        val maxTextWidth = maxTextWidth(lines, bridge)
        val lineStep = TEXT_HEIGHT * max(0f, t.tooltipLineSpacing)
        val textBlockH = textBlockHeight(lines.size, lineStep)
        val tipW = maxTextWidth + t.tooltipPaddingX * 2f
        val tipH = textBlockH + t.tooltipPaddingY * 2f
        val placement = OverlayPlacement.resolveBox(
            box,
            tipW,
            tipH,
            bridge.screenWidth(),
            bridge.screenHeight(),
            OverlayPlacement.Side.BOTTOM,
            0f,
            GAP,
            0f,
            0f,
        )
        val x = placement.x
        val y = placement.y

        drawBackground(bridge, x, y, tipW, tipH, t)
        drawLines(bridge, lines, x + t.tooltipPaddingX, y + t.tooltipPaddingY, lineStep, t.tooltipTextColor)
    }

    private fun splitLines(text: String): Array<String> = text.split("\n").toTypedArray()

    private fun maxTextWidth(lines: Array<String>, bridge: UiRenderBridge): Float {
        var maxWidth = 0f
        for (line in lines) {
            maxWidth = max(maxWidth, bridge.textWidth(line, TEXT_SCALE))
        }
        return maxWidth
    }

    private fun textBlockHeight(lineCount: Int, lineStep: Float): Float {
        if (lineCount <= 0) {
            return 0f
        }
        val safeStep = max(0f, lineStep)
        return max(TEXT_HEIGHT, TEXT_HEIGHT + (lineCount - 1) * safeStep)
    }

    private fun drawBackground(bridge: UiRenderBridge, x: Float, y: Float, tipW: Float, tipH: Float, t: ControlTheme) {
        if (t.tooltipCornerRadius <= 0f) {
            bridge.fillRect(x, y, tipW, tipH, t.tooltipBackgroundColor)
            return
        }
        val fill = StyledFill.builder()
            .cornerRadius(t.tooltipCornerRadius)
            .build()
        val command = RenderCommand.roundedRect(
            x,
            y,
            tipW,
            tipH,
            t.tooltipBackgroundColor,
            RenderCommand.NO_CLIP,
            fill,
        )
        bridge.submitBatch(
            RenderBatch(
                RenderCommandType.ROUNDED_RECT,
                null,
                RenderCommand.NO_CLIP,
                listOf(command),
            ),
        )
    }

    private fun drawLines(
        bridge: UiRenderBridge,
        lines: Array<String>,
        x: Float,
        y: Float,
        lineStep: Float,
        textColor: Int,
    ) {
        for (i in lines.indices) {
            bridge.drawText(lines[i], x, y + i * lineStep, textColor, TEXT_SCALE)
        }
    }

    /**
     * tooltip 候选项：在元素遍历过程中择优记录最深层级、处于悬停态且文本非空的元素。
     */
    internal class Candidate {
        var text: String? = null
            private set
        var box: Box? = null
            private set
        private var depth = -1
        var hoverSinceMs: Long = 0L
            private set

        @JvmName("consider")
        internal fun consider(element: UIElement<*>, box: Box, depth: Int) {
            val value = element.tooltip()
            if (!element.hovered() || value.isNullOrEmpty() || depth < this.depth) {
                return
            }
            this.text = value
            this.box = box
            this.depth = depth
            this.hoverSinceMs = element.hoverSinceMs()
        }
    }

    private fun readyToRender(text: String?, hoverSinceMs: Long, theme: ControlTheme, currentTimeMs: Long): Boolean {
        if (text == null) {
            return false
        }
        if (hoverSinceMs <= 0L) {
            return true
        }
        return currentTimeMs - hoverSinceMs >= theme.tooltipDelayMs
    }
}
